import { forwardRef, useCallback, useRef, useState } from 'react';
import { Input, InputProps } from '../Input/Input';

export interface UnitInputProps extends Omit<InputProps, 'value' | 'defaultValue'> {
  unit: string;
  value?: 'auto' | number;
  defaultValue?: 'auto' | number;
}

const addUnit = (value: unknown, unit: string) => {
  const strValue = String(value);
  if (strValue.endsWith(unit) || value === 'auto') {
    return strValue;
  }
  return `${strValue}${unit}`;
};

const stripUnit = (value: string, unit: string): string => value.replace(unit, '');

// The following input is used to input a number with a unit.
// It is a wrapper around the Input component with a unit label.
export const UnitInput = forwardRef<HTMLInputElement, UnitInputProps>(function UnitInput(
  { unit, onChange, onBlur, onFocus, value, defaultValue, ...props },
  ref,
) {
  const [isEditing, setIsEditing] = useState(false);
  const editValueRef = useRef('');

  const displayValue = isEditing
    ? editValueRef.current
    : value !== undefined
      ? addUnit(value ?? defaultValue ?? 0, unit)
      : '';

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      event.target.value = stripUnit(raw, unit);
      editValueRef.current = raw;
      onChange?.(event);
    },
    [onChange, unit],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      event.target.value = stripUnit(raw, unit);
      editValueRef.current = '';
      setIsEditing(false);
      onBlur?.(event);
    },
    [onBlur, unit],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      editValueRef.current = displayValue;
      setIsEditing(true);
      onFocus?.(event);
    },
    [onFocus, displayValue],
  );

  return (
    <Input
      {...props}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={ref}
      value={displayValue}
    />
  );
});
